import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { InputDialog } from "../customComponents/InputDialog";
import Call from "../../assets/icon/svgs/Call.svg?react";
import userProfileApi from "@/api-client/api/services/userProfile/userProfile";
import OtpInput from "../customComponents/OptInout";
import { AxiosError } from "axios";

export function PopUpVerifiNumber({
  openPopUp,
  onClose,
  fetchUserProfile,
  chanelType,
}: {
  openPopUp: boolean;
  onClose: () => void;
  fetchUserProfile: () => void;
  chanelType: "enable_sms" | "enable_whatsapp" | "enable_telegram" | undefined;
}) {
  const { t } = useTranslation();
  const [number, setNumber] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [numberError, setNumberError] = useState("");
  const [codeError, setCodeError] = useState("");
  const [step, setStep] = useState(1);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [step, timer]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const validateNumber = (number: string): boolean => {
    return /^\+[1-9]\d{7,14}$/.test(number);
  };

  const postVerifyNumberStart = async () => {
    if (!chanelType) return;
    try {
      await userProfileApi.postVerifyNumberStart({
        channel: chanelType.split("_")[1] as "sms" | "whatsapp",
        number: number.replace(/-/g, ""),
      });
      setStep(2);
    } catch (error: unknown) {
      if (
        error instanceof AxiosError &&
        error.response?.data?.error === "This phone number is already registered to another user"
      ) {
        setNumberError(t("popUpVerifiNumber.phone_number_already_registered"));
      } else {
        setNumberError(t("popUpVerifiNumber.something_went_wrong"));
      }
      console.error("Error sending verification code", error);
    }
  };

  const postVerifyNumberConfirm = async () => {
    if (!code || code.length < 6) {
      setCodeError(t("popUpVerifiNumber.invalid_code"));
      return;
    }
    if (!chanelType) return;
    try {
      await userProfileApi.postVerifyNumberConfirm({
        channel: chanelType.split("_")[1] as "sms" | "whatsapp",
        number: number.replace(/-/g, ""),
        code: code,
      });
      onClose();
      fetchUserProfile();
    } catch (error) {
      setCodeError(t("popUpVerifiNumber.invalid_code"));
      console.error("Error confirming code", error);
    }
  };

  const postVerifyNumberResend = async () => {
    if (!chanelType) return;
    try {
      await userProfileApi.postVerifyNumberResend({
        channel: chanelType.split("_")[1] as "sms" | "whatsapp",
        number: number.replace(/-/g, ""),
      });
      setTimer(30);
      setCanResend(false);
    } catch (error) {
      setCodeError(t("popUpVerifiNumber.something_went_wrong_resend_code"));
      console.error("Error resending verification code", error);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    try {
      await postVerifyNumberResend();
    } catch (error) {
      console.error("Error resending verification code", error);
    }
  };

  return (
    <Dialog open={openPopUp} onOpenChange={onClose} modal={false}>
      {openPopUp && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-[4px] z-40 pointer-events-none"></div>
      )}
      <DialogContent
        className="max-h-full overflow-auto gap-4 w-[480px] pb-[40px]"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="mb-[16px] flex-call gap-4">
          <div className="flex flex-row gap-4">
            <Call
              width={32}
              height={32}
              className="text-[#0027CB] whitespace-nowrap shrink-0"
            />
            <div className="flex flex-col gap-2">
              <DialogTitle className="text-[24px] text-left font-medium !m-0">
                {step === 1
                  ? t("popUpVerifiNumber.header")
                  : t("popUpVerifiNumber.code_sent_header")}
              </DialogTitle>
              <DialogDescription className="text-[16px] !m-0 leading-[120%] text-left font-medium text-[#00000080]">
                {step === 1
                  ? t("popUpVerifiNumber.header_description")
                  : t("popUpVerifiNumber.code_sent_description")}
              </DialogDescription>
              {step == 2 && (
                <DialogDescription className="text-[16px] !m-0 leading-[120%] text-left font-medium text-[#00000080]">
                  {t("popUpVerifiNumber.code_sent_description2")}
                </DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-8">
          {/* Step 1: */}
          {step === 1 && (
            <>
              <InputDialog
                id="popUpVerifiNumber.number"
                validationError={numberError}
                placeholder={t("popUpVerifiNumber.your_phone_number")}
                isBiggerInput={true}
                inputValue={number}
                disablePlaceholderAnimation={false}
                setValidationError={(error) => setNumberError(error ?? "")}
                setInputValue={(rawValue) => {
                  let cleaned = rawValue.replace(/[^\d+]/g, "");

                  if (cleaned.startsWith("+")) {
                    cleaned = "+" + cleaned.slice(1).replace(/\+/g, "");
                  } else {
                    cleaned = "+" + cleaned;
                  }

                  const digitsOnly = cleaned.slice(1);

                  let formatted = "+";
                  if (digitsOnly.length > 0)
                    formatted += digitsOnly.slice(0, 3);
                  if (digitsOnly.length > 3)
                    formatted += "-" + digitsOnly.slice(3, 5);
                  if (digitsOnly.length > 5)
                    formatted += "-" + digitsOnly.slice(5, 8);
                  if (digitsOnly.length > 8)
                    formatted += "-" + digitsOnly.slice(8, 12);

                  setNumber(formatted);

                  if (numberError && validateNumber(cleaned)) {
                    setNumberError("");
                  }
                }}
              />

              <Button
                type="submit"
                variant="system"
                size="system"
                disabled={!number}
                onClick={postVerifyNumberStart}
              >
                {t("popUpVerifiNumber.continue")}
              </Button>
            </>
          )}

          {/* Step 2: */}
          {step === 2 && (
            <>
              <div className="flex flex-col gap-1">
                <OtpInput length={6} code={code} setCode={setCode} />
                {codeError && (
                  <span className="text-[16px] text-[#FF0000]">
                    {codeError}
                  </span>
                )}
              </div>
              <Button
                type="submit"
                variant="system"
                size="system"
                disabled={code?.length !== 6}
                onClick={postVerifyNumberConfirm}
              >
                {t("popUpVerifiNumber.confirm")}
              </Button>

              <div className="flex flex-row gap-1 items-center justify-between">
                <div className="flex flex-row gap-4 items-center">
                  <span className="text-[12px] leading-[120%] text-[#00000080] whitespace-pre-line">
                    {t("popUpVerifiNumber.you_can_resend_code")}
                  </span>
                  <span className="text-[24px] font-medium text-[#000000]">
                    {formatTime(timer)}
                  </span>
                </div>
                <Button
                  type="submit"
                  variant="system"
                  className="!h-10 w-fit text-[20px]"
                  disabled={!canResend}
                  onClick={handleResendCode}
                >
                  {t("popUpVerifiNumber.code_resend")}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
